<template>
  <el-container direction="vertical" style="margin: 5vh 5vw">
    <div class="header">
      <h2>Package: {{ currentJobName }}</h2>
    </div>
    <el-breadcrumb
      separator-class="el-icon-arrow-right"
      style="margin: 5px 0 35px 0"
    >
      <el-breadcrumb-item :to="{ path: `/servers/${instanceId}/packages` }"
        >Packages</el-breadcrumb-item
      >
      <el-breadcrumb-item>Schedule</el-breadcrumb-item>
    </el-breadcrumb>
    <div
      style="
        display: flex;
        justify-content: space-between;
        margin: 0 0 10px 10px;
      "
    >
      <el-checkbox
        v-model="scheduled"
        :disabled="envRights === 'Run'"
        @change="saveSchedule"
        >Activate the Package to run when scheduled</el-checkbox
      >
    </div>
    <el-row :gutter="20">
      <el-col :span="12">
        <el-card shadow="hover">
          <el-form label-position="top">
            <el-form-item label="Start Date">
              <el-date-picker
                v-model="date"
                :disabled="envRights === 'Run'"
                :clearable="false"
                @change="dateSelected"
                format="MMMM d, yyyy"
                style="width: 100%"
              />
            </el-form-item>
            <el-form-item label="Frequency">
              <el-select
                filterable
                v-model="frequency"
                :disabled="envRights === 'Run'"
                style="width: 100%"
                @change="scheduled && saveSchedule()"
              >
                <el-option label="Once" value="Once"></el-option>
                <el-option label="Hourly" value="Hourly"></el-option>
                <el-option label="Daily" value="Daily"></el-option>
                <el-option label="Weekly" value="Weekly"></el-option>
                <el-option label="Monthly" value="Monthly"></el-option>
              </el-select>
            </el-form-item>
          </el-form>
        </el-card>
      </el-col>
      <el-col :span="12">
        <el-card shadow="hover">
          <el-form label-position="top">
            <el-form-item :label="`Start Time (${timezone.replace('_', ' ')})`">
              <el-time-picker
                v-model="time"
                :disabled="envRights === 'Run'"
                :clearable="false"
                @change="timeSelected"
                format="h:mm:ss A"
                style="width: 100%"
              ></el-time-picker>
            </el-form-item>
            <el-form-item>
              <span slot="label">
                UTC Time
                <small>
                  <el-tooltip placement="right" effect="light">
                    <span slot="content"
                      >UTC stands for Coordinated Universal Time, a standard<br />used
                      to set all time zones around the world.</span
                    >
                    <i
                      class="el-icon-info"
                      style="position: relative; left: 2px; bottom: 4px"
                    />
                  </el-tooltip>
                </small>
              </span>
              <el-time-picker
                format="h:mm:ss A"
                v-model="timeUTC"
                style="width: 100%"
                disabled
              ></el-time-picker>
            </el-form-item>
          </el-form>
        </el-card>
      </el-col>
    </el-row>
  </el-container>
</template>

<script>
import { mapState } from "vuex";
import JobService from "../services/JobService";
import moment from "moment-timezone";
import Helper from "../utilities/Helper";

export default {
  computed: {
    ...mapState([
      "jobId",
      "userId",
      "currentJobName",
      "instanceId",
      "envRights",
      "isUserLoggedIn",
    ]),
  },
  data() {
    return {
      date: moment(),
      time: moment(),
      timeUTC: moment.utc().format("YYYY-MM-DD HH:mm:ss"),
      scheduled: false,
      frequency: "Once",
      timezone:
        process.env.VUE_APP_SERVER_TIMEZONE ||
        Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  },
  methods: {
    handleError: Helper.handleError,
    /**
     * Resets the schedule input interface
     * @function
     */
    async resetSchedule() {
      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        moment.tz.setDefault(this.timezone);

        const response = await JobService.getDetails({
          jobId: this.jobId,
        });
        const job = response.data.job;
        if (job.scheduleStartDateTime) {
          const dateTime = moment(job.scheduleStartDateTime);
          this.date = dateTime.tz(this.timezone).format("YYYY-MM-DD HH:mm:ss");
          this.time = dateTime.tz(this.timezone).format("YYYY-MM-DD HH:mm:ss");
          this.timeUTC = moment()
            .year(dateTime.year())
            .month(dateTime.month())
            .date(dateTime.date())
            .hour(dateTime.hours())
            .minute(dateTime.minutes())
            .second(0)
            .millisecond(0)
            .utc()
            .format("YYYY-MM-DD HH:mm:ss");

          this.scheduled = job.scheduled;
          this.frequency = job.scheduleFrequency;
        } else {
          this.date = moment().tz(this.timezone).format("YYYY-MM-DD HH:mm:ss");
          this.time = moment().tz(this.timezone).format("YYYY-MM-DD HH:mm:ss");
        }
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
    /**
     * Saves the schedule for a particular package
     * @function
     */
    async saveSchedule() {
      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        const date = !moment.isMoment(this.date)
          ? moment(this.date).format("YYYY-MM-DD")
          : this.date.format("YYYY-MM-DD");
        const time = !moment.isMoment(this.time)
          ? moment(this.time).format("HH:mm:ss")
          : this.time.format("HH:mm:ss");

        const scheduleStartDateTime =
          process.env.VUE_APP_ENVIRONMENT === "production"
            ? moment(date + " " + time)
                .utc()
                .format("YYYY-MM-DD HH:mm:ss")
            : date + " " + time;

        await JobService.saveSchedule({
          jobId: this.jobId,
          scheduled: this.scheduled,
          scheduleStartDateTime,
          scheduleFrequency: this.frequency,
          updatedBy: this.userId,
        });

        this.$message({
          type: "success",
          message: `"${this.currentJobName}" package schedule saved successfully`,
          duration: 5000,
          showClose: true,
        });
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
    /**
     * Updates the UTC time when a new local date is selected
     * @function
     */
    dateSelected(context) {
      if (!moment.isMoment(this.time)) this.time = moment(this.time);
      this.timeUTC = moment()
        .year(context.getFullYear())
        .month(context.getMonth())
        .date(context.getDate())
        .hour(this.time.hours())
        .minute(this.time.minutes())
        .second(0)
        .millisecond(0)
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");
      if (this.scheduled) this.saveSchedule();
    },
    /**
     * Updates the UTC time when a new local time is selected
     */
    timeSelected(context) {
      if (!moment.isMoment(this.date)) this.date = moment(this.date);
      this.timeUTC = moment()
        .year(this.date.year())
        .month(this.date.month())
        .date(this.date.date())
        .hour(context.getHours())
        .minute(context.getMinutes())
        .second(0)
        .millisecond(0)
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");
      if (this.scheduled) this.saveSchedule();
    },
  },
  mounted() {
    if (!this.isUserLoggedIn) {
      this.$router.push({ name: "login" });
    } else if (!this.instanceId) {
      this.$router.push({ name: "servers" });
    } else {
      this.resetSchedule();
    }
  },
  beforeDestroy() {
    if (this.jobId) this.$store.dispatch("setJobId", null);
    if (this.currentJobName) this.$store.dispatch("setCurrentJobName", null);
  },
};
</script>

<style scoped>
.error :deep(.el-input__inner) {
  border-color: red;
}
.button {
  background-color: #b3282d;
  color: white;
  border-color: white;
}
.button:focus {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
.button:hover {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
</style>
